/**
 * Per-leg camera coverage footprint approximation.
 *
 * For each mission leg we compute a single elliptical ground patch
 * representing roughly what the camera sees on that leg. v1 is a coarse
 * visualization, not a photogrammetry-grade frustum projection:
 *
 *   - Footprint center = leg midpoint offset forward along the camera's
 *     yaw by `alt / tan(-pitch)` meters (i.e., where the camera's optical
 *     axis hits the ground).
 *   - Footprint size = scaled by altitude and horizontal FOV. We don't
 *     project the full 3-D camera frustum; instead we approximate the
 *     footprint as an ellipse with the long axis along yaw and the
 *     half-width = alt × tan(fovH/2).
 *
 * Edge cases:
 *   - Near-horizontal pitch (-10° to 0°) → no footprint rendered. The
 *     true projection extends to the horizon, which would dominate the
 *     map and isn't useful for the inspection demo.
 *   - Near-nadir pitch (≤ -85°) → footprint centered at the waypoint
 *     itself (no forward offset). Avoids the tan-singularity at -90°.
 *
 * M3T wide-lens FOV (≈84° horizontal × 70° vertical) is the default
 * sensor preset; callers can override per-call for other lens configs.
 */

import type { DemoMission, DemoWaypoint } from '../components/dock-demo/types';
import { resolveEffectiveLegParams } from './missionValidation';

type Point = [number, number];

const M_PER_DEG_LAT = 111_000;

function mPerDegLng(latDeg: number): number {
  return M_PER_DEG_LAT * Math.cos((latDeg * Math.PI) / 180);
}

function bearingDeg(a: Point, b: Point): number {
  const lng1 = (a[0] * Math.PI) / 180;
  const lat1 = (a[1] * Math.PI) / 180;
  const lng2 = (b[0] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const y = Math.sin(lng2 - lng1) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(lng2 - lng1);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

export interface CoverageOptions {
  /** Horizontal field of view in degrees. Default 84° (M3T wide). */
  fovH?: number;
  /** Number of points around the ellipse. Default 24 (smooth at typical
   *  map zooms; raise for very large footprints). */
  steps?: number;
}

/** Compute a single leg's camera coverage polygon, or null when the camera
 *  is pointing too close to horizontal for a meaningful ground footprint. */
export function legCoveragePolygon(
  legA: Point,
  legB: Point,
  altM: number,
  gimbalPitchDeg: number,
  gimbalYawDeg: number,
  opts: CoverageOptions = {},
): Point[] | null {
  const fovH = opts.fovH ?? 84;
  const steps = opts.steps ?? 24;

  // Pitch convention: -90° = straight down, 0° = horizontal, +30° = upward.
  // We need pitch ≤ -10° for a sensible ground footprint; near-horizontal
  // shots extend to the horizon and would dominate the visualization.
  if (gimbalPitchDeg > -10 || altM <= 0) return null;

  const midX = (legA[0] + legB[0]) / 2;
  const midY = (legA[1] + legB[1]) / 2;
  const mPerLng = mPerDegLng(midY);

  const yawRad = (gimbalYawDeg * Math.PI) / 180;
  // Clamp pitch away from -90 to dodge the tan-singularity at nadir; from
  // ≤ -85° we treat the camera as fully nadir-looking and skip the forward
  // offset entirely (footprint is centered directly under the camera).
  const isNadir = gimbalPitchDeg <= -85;
  const pitchClamped = Math.min(-10, Math.max(-89, gimbalPitchDeg));
  const fwdDistM = isNadir
    ? 0
    : altM / Math.tan((-pitchClamped * Math.PI) / 180);

  // Footprint center — leg midpoint shifted forward along yaw.
  const cxM = fwdDistM * Math.sin(yawRad);
  const cyM = fwdDistM * Math.cos(yawRad);
  const centerLng = midX + cxM / mPerLng;
  const centerLat = midY + cyM / M_PER_DEG_LAT;

  // Footprint half-axes. Width derived from horizontal FOV at the slant
  // range; length is 70% of width for a roughly camera-like aspect ratio
  // (matches the M3T wide-lens 84°×70° split). The slant range for nadir
  // is just altitude; oblique uses the hypotenuse.
  const slantM = isNadir ? altM : altM / Math.sin((-pitchClamped * Math.PI) / 180);
  const halfWidthM = slantM * Math.tan((fovH / 2) * Math.PI / 180);
  const halfLengthM = halfWidthM * 0.7;

  // Build the ellipse in a local meters frame aligned with yaw, then rotate
  // back to compass orientation and reproject to lng/lat.
  const cosY = Math.cos(yawRad);
  const sinY = Math.sin(yawRad);
  const points: Point[] = [];
  for (let i = 0; i < steps; i++) {
    const a = (i / steps) * 2 * Math.PI;
    // Local frame: x = lateral (cross-yaw), y = forward (along yaw).
    const lx = halfWidthM * Math.cos(a);
    const ly = halfLengthM * Math.sin(a);
    // Rotate by yaw so the forward axis points along the camera's compass
    // heading. yaw=0=N (positive y in lng/lat), 90=E (positive x).
    const wx = lx * cosY + ly * sinY;
    const wy = -lx * sinY + ly * cosY;
    points.push([centerLng + wx / mPerLng, centerLat + wy / M_PER_DEG_LAT]);
  }
  return points;
}

export interface MissionCoverage {
  legIndex: number;
  /** Closed ring of [lng, lat] vertices for this leg's footprint. */
  polygon: Point[];
}

/** Build coverage polygons for every leg in a mission. Legs whose camera
 *  config is too horizontal (pitch > -10°) or whose altitude is zero get
 *  dropped — the layer will simply not render those. */
export function buildMissionCoverage(mission: DemoMission): MissionCoverage[] {
  const wps = mission.waypoints;
  if (wps.length < 2) return [];
  const out: MissionCoverage[] = [];
  for (let i = 0; i < wps.length - 1; i++) {
    const lp = resolveEffectiveLegParams(wps, i);
    const a: Point = [wps[i].lng, wps[i].lat];
    const b: Point = [wps[i + 1].lng, wps[i + 1].lat];
    // Effective gimbal yaw: explicit value, or fall back to the leg's
    // bearing (DJI's "follow aircraft heading" default). This matches the
    // simulator's gimbal-yaw resolution at runtime.
    const yaw = lp.gimbalYaw ?? bearingDeg(a, b);
    const pitch = lp.gimbalPitch ?? -45; // sensible inspection default
    const alt = avgWaypointAlt(wps[i], wps[i + 1]);
    const poly = legCoveragePolygon(a, b, alt, pitch, yaw);
    if (poly) out.push({ legIndex: i, polygon: poly });
  }
  return out;
}

function avgWaypointAlt(a: DemoWaypoint, b: DemoWaypoint): number {
  // Simple linear average of the two endpoint altitudes. For legs where
  // the drone climbs / descends, this approximates the camera height at
  // the leg midpoint.
  return (a.alt + b.alt) / 2;
}
